// The image of the cross: renderer, studio lighting, materials, model, and the
// MeshTransmissionMaterial render protocol. Shared by the landing page and the
// studio so both always render identically. Page behaviour (scroll, input,
// timing) lives with each page, not here.
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MeshTransmissionMaterial } from "@pmndrs/vanilla";
import { QUALITY_DEFAULTS, resolveQuality } from "./quality.js";
import { FIELD_PARS, bakeDistortionField, fieldMatrix, withBakedDistortion } from "./distortion-field.js";
import { BUFFER_TEXTURE, createCustomGlass } from "./glass/custom-glass.js";
import { resolveGlass } from "./glass/look.js";
import { ORANGE_PEEL_SIZE, orangePeelNormals } from "./orange-peel.js";

const FRONT_THICKNESS = 0.4;  // lower = less refraction blur = clearer interior
const BACKSIDE_THICKNESS = 0.25;

// LED glow (setGlow). The drive's activity LED sits on the front of the USB
// body, inside the stem, LED_HEIGHT_MM above the bottom of the resin (the
// body's top is at 20.7 mm). The page doesn't use the number: the Blender
// bakes read it from here, the single source of truth. blender/glow.py bakes
// how the light travels through the resin into assets/glow.png, and
// blender/usb.py puts the LED on the drive's texture (assets/usb-led.png):
// rerun both after changing it (then `npm run bakes`, which encodes the WebPs
// the page loads).
const LED_HEIGHT_MM = 18;
const GLOW_COLOR = new THREE.Color(1, 0.035, 0.03); // linear: a ~630 nm red LED
// weights of the bake's channels (see GLOW_FRAGMENT) and the LED itself
const GLOW_VOLUME = 0.1;
const GLOW_ESCAPE = 16;
const GLOW_GRAZING = 0.5;
const GLOW_PEAK = 2.5; // the brightest the resin's red gets (linear)
const LED_INTENSITY = 1.5;

/** Every file the scene loads, by the name createCrossScene's assetUrl takes. */
export const SCENE_FILES = Object.freeze(["cross.glb", "usb.glb", "ao.webp", "cavity.webp", "usb-color.webp", "usb-orm.webp", "usb-led.webp", "glow.webp"]);

// Builds the scene into `canvas`. Call setSize() before the first render().
//   assetUrl  — (file) => the URL of one of the scene's assets ("cross.glb",
//               "ao.webp", ...); default assetBase + file. The store's theme
//               maps each to its own theme asset URL (js/theme.js)
//   model     — another GLB to load in place of the cross (a URL; default
//               the cross, assetUrl("cross.glb"))
//   responses — { [file]: promise of its Response }: the page's own fetches
//               of some of the scene's files (named as assetUrl takes them),
//               started before this module could (index.html, shopify/
//               sections/nothing-cross.liquid: why there, see index.html).
//               Each is read from its response rather than fetched again; a
//               file without one is fetched here, as is a `model` in the
//               cross's place (default: none, the studio's case)
//   antialias — the canvas's MSAA; fixed for the life of the WebGL context
//   glass     — which glass, { kind: "mtm" | "custom", look } (js/glass/look.js;
//               default the site's MTM glass); setGlass() changes it later
//   arriveAfter — a dev option (the page's ?delay): the models and textures
//               count as arrived (their promises below resolve) no sooner than
//               this many ms after navigation start, as on a slow connection.
//               Default 0: as soon as they are here
// Returns { renderer, scene, camera, group, setSize, setBackground, setGlow,
// loadGlow, render, quality, setQuality, glass, setGlass, prepareGlass, transmissionTargets,
// distortionField, modelLoaded, downloaded, ready }:
//   setGlow(amount) — 0..1, the red LED lighting the resin (0 = off, default).
//                 The resin's glow shows once its bake has loaded: a glow set
//                 earlier lights up when the bake lands. Lighting it starts
//                 the load. The LED itself draws with the drive's own
//                 programs (makeUsbMaterials): lighting it compiles nothing.
//   loadGlow()  — starts loading the glow's bake (once; later calls return the
//                 same promise); resolves when it is in place, so the next
//                 render() shows it. Rejects if it fails to load.
//   quality     — the current cost knobs (frozen; see js/quality.js)
//   setQuality(partial) — changes some of them; returns the new quality
//   glass       — the current glass setting (frozen; js/glass/look.js)
//   setGlass(setting) — switches the glass and/or the custom glass's look;
//                 returns the new setting. A glass never used before compiles
//                 its program on the next render(), unless prepared:
//   prepareGlass(setting) — compiles that glass's programs without
//                 switching to it (off the main thread where the browser
//                 can); resolves once they are ready (rejects if the WebGL
//                 context is lost meanwhile). Needs the model in
//   transmissionTargets — { main, back }: the render targets of passes 1 and
//                 2 (see render), for instrumentation
//   distortionField — the baked distortion (js/distortion-field.js), once
//                 distortionMode "baked" has made it; for tests
//   modelLoaded — resolves once the GLB is in the scene (textures may still be loading)
//   downloaded  — resolves once the GLBs and every texture the unlit cross
//                 needs have loaded: all that is left before ready is the
//                 shaders' compile
//   ready       — resolves once the GLB and every texture the unlit cross
//                 needs have loaded and the shaders render() needs have
//                 compiled (the glow's bake is not waited for); rejects if
//                 the WebGL context is lost before then
// All three reject if any asset fails to load.
export function createCrossScene({ canvas, assetBase = "assets/", assetUrl = (file) => assetBase + file, model: modelUrl = null, responses = null, antialias = true, glass: initialGlass = { kind: "mtm" }, arriveAfter = 0 }) {
  // an asset's load, held to arriveAfter (see above)
  const arrival = (load) => (arriveAfter > 0
    ? load.then((asset) => new Promise((resolve) => setTimeout(() => resolve(asset), arriveAfter - performance.now())))
    : load);
  // one of the scene's files: the page's own response for it (see
  // `responses`), or fetched now
  for (const file of Object.keys(responses ?? {})) {
    if (!SCENE_FILES.includes(file)) throw new Error(`responses: "${file}" is not one of the scene's files (${SCENE_FILES.join(", ")})`);
  }
  const assetResponse = (file) => responses?.[file] ?? fetch(assetUrl(file));
  const isMobile = matchMedia("(pointer: coarse)").matches;
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias,
    powerPreference: "high-performance",
  });
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  // the transmission buffer is the expensive part of glass; the frost blurs
  // it anyway, so render it at reduced resolution
  if ("transmissionResolutionScale" in renderer) {
    renderer.transmissionResolutionScale = isMobile ? 0.5 : 0.7;
  }
  // The WebGL context can be lost at any time (the browser restarts its GPU
  // process: a GPU hang, its watchdog on a starved machine, memory
  // pressure); three asks for it back and, restored, starts with nothing on
  // the GPU. Everything the scene made there before (the compiled programs,
  // the uploads, the environment's and the distortion's bakes, a fence) is
  // gone. contextLosses counts the losses; see programsReady and `ready`.
  let contextLosses = 0;
  let firstLoss;
  const contextLost = new Promise((resolve) => { firstLoss = resolve; });
  canvas.addEventListener("webglcontextlost", () => {
    contextLosses++;
    firstLoss();
  });

  const scene = new THREE.Scene();

  // Bright backdrop: frosted resin reads icy against light, metallic against dark
  const bgCanvas = document.createElement("canvas");
  bgCanvas.width = bgCanvas.height = 512;
  const bctx = bgCanvas.getContext("2d");
  const grad = bctx.createRadialGradient(256, 215, 60, 256, 256, 420);
  grad.addColorStop(0, "#ffffff");
  grad.addColorStop(0.5, "#dfdfe3");
  grad.addColorStop(1, "#b7b7bf");
  bctx.fillStyle = grad;
  bctx.fillRect(0, 0, 512, 512);
  const bgTex = new THREE.CanvasTexture(bgCanvas);
  bgTex.colorSpace = THREE.SRGBColorSpace;
  // drawn by a mesh of ours rather than as scene.background: see makeBackdrop
  const backdrop = makeBackdrop(bgTex);
  scene.add(backdrop);

  // Glass-studio environment: strip softboxes with dark gaps between them
  // (dark-field lighting). Every ridge facet mirrors either a bright strip or
  // blackness, so facets separate instead of blending. An even room HDRI
  // can't do this — the gaps are the point.
  function bakeEnvironment() {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envScene = new THREE.Scene();
    const strip = (w, h, i) => {
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(i) })
      );
      envScene.add(m);
      return m;
    };
    // vertical strips around the object -> separates VERTICAL ridge facets
    const verticals = [
      { a: 0.6, i: 10 },  // key, right-front
      { a: -0.7, i: 3.5 },  // fill, left-front
      { a: 1.6, i: 6 },  // right edge
      { a: -1.7, i: 5 },  // left edge
      { a: Math.PI, i: 4 },  // back rim
    ];
    for (const { a, i } of verticals) {
      const s = strip(2.2, 14, i);
      s.position.set(Math.sin(a) * 9, 0, Math.cos(a) * 9);
      s.lookAt(0, 0, 0);
    }
    // horizontal strips above/below -> separates HORIZONTAL ridge facets
    const top = strip(16, 3, 3.5);
    top.position.set(0, 9, 2);
    top.lookAt(0, 0, 0);
    const bot = strip(16, 2.5, 1.2);
    bot.position.set(0, -9, 3);
    bot.lookAt(0, 0, 0);
    const texture = pmrem.fromScene(envScene, 0.02).texture;
    // the environment map is all that's kept: free the generator's working
    // targets and shaders, and the strips' GPU buffers
    pmrem.dispose();
    envScene.traverse((o) => {
      if (o.isMesh) {
        o.geometry.dispose();
        o.material.dispose();
      }
    });
    return texture;
  }
  // Baked before the shaders' compiles are issued (see the early compile
  // below), not after: its three programs link synchronously, and issued
  // behind the scene's they waited for them, blocking the main thread
  // ~500 ms (perf/startup.mjs, cold first visits).
  scene.environment = bakeEnvironment();

  // aspect is set by setSize()
  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  camera.position.set(0, 0, 7.4);

  // the strip environment does the heavy lifting; these just add gentle shape
  const key = new THREE.DirectionalLight(0xffffff, 0.6);
  key.position.set(7, 2, 3);
  scene.add(key);

  const rim = new THREE.DirectionalLight(0xffffff, 0.25);
  rim.position.set(-4, -2, -3);
  scene.add(rim);

  // --- materials ---------------------------------------------------------

  const peel = makeOrangePeel();

  // SLA clear resin via MeshTransmissionMaterial (drei's, vanilla port).
  // Its backside pass refracts the object's own interior — real two-interface
  // refraction — and its per-sample chromatic aberration is far smoother than
  // native dispersion. Tuned CLEAR: low roughness/blur, mild distortion noise
  // for the printed-not-polished refraction wobble.
  // baked from the mesh in Blender: groove shadows. The page loads the
  // bakes as lossless WebPs of Blender's PNGs (scripts/encode-bakes.mjs).
  // (No thickness map: three
  // only samples one when material.transmission > 0, which the transmission
  // protocol below forces to 0.)
  // Each texture is fetched (or taken from the page's own early fetch of it,
  // see `responses`), decoded off the main thread (decodeImage), and
  // uploaded (initTexture) once it has arrived AND the programs queued so
  // far have compiled (see uploadWhenCompiled), rather than in the first
  // frame. The material needs the texture object now; `arrived` reports when
  // (and whether) its image arrived, at its (possibly held, see arriveAfter)
  // arrival so ?delay plays out like a slow network. Decoded as stored: its
  // rows top-down (flipY false: an ImageBitmap can't be flipped at upload),
  // no premultiplied alpha, no color conversion.
  const textureArrivals = [];
  const textureUploads = [];
  // `response`: the file's response to read (default: the page's early
  // fetch of it, or a fetch now: assetResponse)
  function fetchTexture(file, response = assetResponse(file)) {
    const url = assetUrl(file);
    const texture = new THREE.Texture();
    texture.flipY = false;
    const arrived = arrival(response.then(
      (res) => {
        if (!res.ok) throw new Error(`failed to load texture ${url} (HTTP ${res.status})`);
        return res.blob();
      },
      (err) => { throw new Error(`failed to load texture ${url}`, { cause: err }); },
    ).then(decodeImage).then((image) => {
      texture.image = image;
      texture.needsUpdate = true;
      return texture;
    }));
    return { texture, arrived };
  }
  const loadTexture = (file) => {
    const { texture, arrived } = fetchTexture(file);
    textureArrivals.push(arrived);
    textureUploads.push(uploadWhenCompiled(arrived));
    return texture;
  };
  // An upload waits for the GPU process, which compiles the shaders: issued
  // while the compiles ran, the first big one blocked the main thread until
  // they were done (the cavity bake's: ~1.1 s on the M1 Max, cold; the page
  // froze, scroll handlers and all). After them it takes a few ms.
  // `compiled` settles once the first compiles (issued below, before the
  // model is in) are ready (rejects: the context was lost meanwhile).
  let compilesDone;
  let compilesLost;
  const compiled = new Promise((resolve, reject) => {
    compilesDone = resolve;
    compilesLost = reject;
  });
  function uploadWhenCompiled(arrived) {
    return Promise.all([arrived, compiled]).then(([texture]) => renderer.initTexture(texture));
  }
  const aoTex = loadTexture("ao.webp");
  // short-ray cavity bake tinted into the surface color: groove lines stay
  // visible regardless of lighting angle (this is how the scans read)
  const cavityTex = loadTexture("cavity.webp");

  let glass = new MeshTransmissionMaterial({ samples: QUALITY_DEFAULTS.samples });
  Object.assign(glass, {
    // nano-texture matte: diffuse surface sheen, no wet gloss — the "glass"
    // is all in the transmission/refraction, not the reflection
    roughness: 0.19,
    metalness: 0,
    ior: 1.5,
    thickness: 0.4,
    chromaticAberration: QUALITY_DEFAULTS.dispersionStrength,
    anisotropicBlur: 0.04,
    distortion: 0.02,
    distortionScale: 2.0,
    temporalDistortion: 0,
    clearcoat: 0,
    clearcoatRoughness: 0.5,
    normalMap: peel,
    clearcoatNormalMap: peel,
    map: cavityTex,
    aoMap: aoTex,
    aoMapIntensity: 1.3,
    attenuationColor: new THREE.Color(0xf9fafa),
    attenuationDistance: 5,
    envMapIntensity: 0.52,
  });
  glass.normalScale.set(0.12, 0.12);
  const DISTORTION = glass.distortion; // the distortion knob's live/baked strength
  // CRITICAL: keep three's native transmission pass OFF — our manual FBO
  // protocol replaces it. If this material reports transmission > 0 the
  // renderer adds its own extra scene passes on top of ours.
  glass.transmission = 0;
  glass._transmission = 1;

  // The glow's emission lives in the material's emissive slot: the bake rides
  // in as emissiveMap and GLOW_FRAGMENT replaces how it is applied. The glow
  // path is compiled once; setGlow() only writes the glowAmount uniform, and
  // at 0 the emission is exactly zero, so the unlit render is unchanged.
  // The bake isn't needed for the first frame, so it loads on request
  // (loadGlow). Until it arrives the slot holds a 1×1 black placeholder: a
  // shader's program depends on whether the slot is filled, not on what fills
  // it, so swapping the bake in compiles nothing; and black emits nothing.
  const glowPlaceholder = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
  glowPlaceholder.needsUpdate = true;
  glass.emissiveMap = glowPlaceholder;
  let glowLoad = null;
  let glowMap = glowPlaceholder; // what the custom glass gets when it is made
  function loadGlow() {
    glowLoad ??= loadGlowTexture().then(putGlow);
    return glowLoad;
  }
  function putGlow(tex) {
    // every glass variant (setQuality swaps them), and those copied later
    // copy it from the default one
    for (const material of glassBySamples.values()) material.emissiveMap = tex;
    glowMap = tex;
    custom?.setGlowMap(tex);
    glowPlaceholder.dispose();
  }

  // The glow's bake, fetched and decoded off the main thread and uploaded as
  // the first frame's textures are: not while the first compiles run (see
  // uploadWhenCompiled), and not in the first frame that shows it.
  function loadGlowTexture() {
    const { texture, arrived } = fetchTexture("glow.webp", fetch(assetUrl("glow.webp")));
    texture.colorSpace = THREE.SRGBColorSpace; // the bake is stored sRGB-encoded
    return uploadWhenCompiled(arrived).then(() => texture);
  }
  const glowUniforms = {
    glowAmount: { value: 0 },
    glowColor: { value: GLOW_COLOR },
    glowWeights: { value: new THREE.Vector3(GLOW_VOLUME, GLOW_ESCAPE, GLOW_GRAZING) },
  };
  // the cavity map's darkening, scaled (the cavityStrength knob; see CAVITY_FRAGMENT)
  const cavityUniforms = { cavityStrength: { value: QUALITY_DEFAULTS.cavityStrength } };
  // the baked distortion's field (distortionMode "baked"; js/distortion-field.js),
  // baked on first use; the matrix follows the pose in render()
  const distortionUniforms = { distortionField: { value: null }, distortionToField: { value: new THREE.Matrix4() } };
  let distortionField = null; // { target, box }
  let distortionAtlas = null; // { target, box, dims }: the custom glass's
  let quality = resolveQuality(QUALITY_DEFAULTS, {});
  // MeshTransmissionMaterial writes its sample count into its shader source
  // when it is constructed, and three's program cache key can't see that: the
  // key names the count (and the dispersion knob, which patches the source
  // too), so materials with different counts never share a program.
  function extendShader(material, samples) {
    const compileTransmission = material.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      // MeshTransmissionMaterial's adds its USE_TRANSMISSION define to
      // shader.defines, which is the material's own defines object: there it
      // would stay, so the material's next program lookup (three keys
      // programs by material.defines, read before onBeforeCompile) would miss
      // and compile the same program again: the first frame did, ~125 ms of
      // a cold first visit. The define goes to this compile's copy only.
      shader.defines = { ...shader.defines };
      compileTransmission(shader, renderer);
      Object.assign(shader.uniforms, glowUniforms, cavityUniforms, distortionUniforms);
      let fragment = replaceChunk(shader.fragmentShader, "#include <emissivemap_fragment>", GLOW_FRAGMENT);
      fragment = replaceChunk(fragment, "#include <map_fragment>", CAVITY_FRAGMENT);
      fragment = replaceChunk(fragment, ISINF_ATTENUATION, FINITE_ATTENUATION);
      if (!quality.dispersion) fragment = withoutDispersion(fragment);
      if (quality.distortionMode === "baked") fragment = FIELD_PARS + withBakedDistortion(fragment);
      shader.fragmentShader = GLOW_PARS + CAVITY_PARS + fragment;
    };
    material.customProgramCacheKey = () =>
      `cross-glass:samples=${samples}:dispersion=${quality.dispersion}:bakedDistortion=${quality.distortionMode === "baked"}`;
  }
  extendShader(glass, QUALITY_DEFAULTS.samples);

  // the glass at each sample count used so far (setQuality swaps them),
  // copied from the one above: three's own properties by copy(), the
  // transmission material's uniform-backed ones by value
  const glassBySamples = new Map([[QUALITY_DEFAULTS.samples, glass]]);
  function glassWithSamples(samples) {
    let material = glassBySamples.get(samples);
    if (!material) {
      const source = glassBySamples.get(QUALITY_DEFAULTS.samples);
      material = new MeshTransmissionMaterial({ samples }).copy(source);
      for (const [name, uniform] of Object.entries(source.uniforms)) material.uniforms[name].value = uniform.value;
      extendShader(material, samples);
      glassBySamples.set(samples, material);
    }
    return material;
  }

  // The custom glass (js/glass/custom-glass.js), made the first time it is
  // used: a page on one glass never compiles the other
  let glassSetting = resolveGlass(initialGlass);
  let custom = null;
  function customGlass() {
    if (!custom) {
      custom = createCustomGlass({
        maps: { normalMap: peel, aoMap: aoTex, map: cavityTex },
        glow: { uniforms: glowUniforms, peak: GLOW_PEAK, map: glowMap },
        distortion: { toField: distortionUniforms.distortionToField },
        lights: [key, rim],
        look: glassSetting.look,
      });
      custom.setQuality(quality);
      custom.setEnvironment(scene.environment);
    }
    return custom;
  }
  const isCustom = () => glassSetting.kind === "custom";
  const glassMaterial = () => (isCustom() ? customGlass().material : glass);
  // the baked distortion: the MTM glass's 3D field for distortionMode
  // "baked"; the custom glass, having no live noise, reads it (as an atlas)
  // unless the distortion is off
  const needsField = () => (quality.distortionMode === "baked" && !distortionField) || (isCustom() && quality.distortionMode !== "off" && !distortionAtlas);

  // FBOs for the two-pass transmission protocol (sized by setSize)
  const fboOpts = { type: THREE.HalfFloatType };
  const fboMain = new THREE.WebGLRenderTarget(1, 1, fboOpts);
  const fboBack = new THREE.WebGLRenderTarget(1, 1, fboOpts);
  // The custom glass reads its buffers' mip chains; MeshTransmissionMaterial
  // reads level 0 only (with mips its lookups would blur where the
  // refraction is steep). A texture's levels are fixed when it is allocated:
  // a change frees the targets, and the next render allocates them again.
  const plainBuffer = { generateMipmaps: fboMain.texture.generateMipmaps, minFilter: fboMain.texture.minFilter };
  function configureBuffers() {
    const wanted = isCustom() ? BUFFER_TEXTURE : plainBuffer;
    for (const target of [fboMain, fboBack]) {
      if (target.texture.minFilter === wanted.minFilter && target.texture.generateMipmaps === wanted.generateMipmaps) continue;
      Object.assign(target.texture, wanted);
      target.dispose();
    }
  }
  configureBuffers();

  const glassMeshes = [];
  const overlayMeshes = [];
  const usbLook = makeUsbMaterials(loadTexture);
  usbLook.setEnvironment(scene.environment);

  // The shaders' compiles start now, before the model has arrived: on
  // stand-in meshes three can't tell from the real ones (three keys a
  // program on the material, the lights, the environment and the mesh's
  // vertex attributes, not on its data). Where the browser compiles in parallel
  // (KHR_parallel_shader_compile) this only queues the work; once the model
  // is in, compileShaders() finds every program already made.
  {
    const geometry = standInGeometry();
    const standIns = new THREE.Group();
    for (const material of [glassMaterial(), usbLook.metal, backdrop.material]) standIns.add(new THREE.Mesh(geometry, material));
    issueCompiles(standIns);
    geometry.dispose();
  }
  programsReady().then(compilesDone, compilesLost);
  // The orange peel's texels, computed (~25-35 ms of arithmetic on a fast
  // laptop) once the compiles are queued, while they run, and uploaded once
  // they are done (see uploadWhenCompiled): a program depends on the normal
  // map being there, not on what it holds.
  const peelFilled = uploadWhenCompiled(new Promise((resolve) => setTimeout(() => {
    fillOrangePeel(peel);
    resolve(peel);
  })));

  // --- model -------------------------------------------------------------

  const group = new THREE.Group();
  scene.add(group);

  const loader = new GLTFLoader();
  const loadModel = (url, response) => arrival(response.then(
    (res) => {
      if (!res.ok) throw new Error(`failed to load model ${url} (HTTP ${res.status})`);
      return res.arrayBuffer();
    },
    (err) => { throw new Error(`failed to load model ${url}`, { cause: err }); },
  ).then((data) => loader.parseAsync(data, THREE.LoaderUtils.extractUrlBase(url))));
  const modelLoaded = Promise.all([
    modelUrl ? loadModel(modelUrl, fetch(modelUrl)) : loadModel(assetUrl("cross.glb"), assetResponse("cross.glb")),
    loadModel(assetUrl("usb.glb"), assetResponse("usb.glb")),
  ]).then(([gltf, usbGltf]) => {
    const model = gltf.scene;
    model.traverse((o) => {
      if (o.isMesh) {
        o.material = glassMaterial();
        glassMeshes.push(o);
      }
    });

    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const s = 3.3 / size.y;          // cross is 86.9 mm tall -> 3.3 world units
    const mm2u = s * (size.y * 1000 / 86.9) / 1000; // world units per mm

    const pivot = new THREE.Group();
    model.position.sub(center);
    pivot.scale.setScalar(s);
    pivot.add(model);
    group.add(pivot);

    const bottomY = -(size.y * s) / 2;
    group.add(placeUSB(usbGltf.scene, mm2u, bottomY, usbLook, overlayMeshes));
    if (needsField()) bakeDistortion();
  });

  // each layout once, the first time the glass is there and needs it
  // (needsField). The custom glass's noise has MTM's scale.
  function bakeDistortion() {
    if (glass.temporalDistortion !== 0) throw new Error("baked distortion: the bake is of a still field (temporalDistortion 0)");
    if (quality.distortionMode === "baked" && !distortionField) {
      distortionField = bakeDistortionField(renderer, group, glassMeshes, glass.distortionScale);
      distortionUniforms.distortionField.value = distortionField.target.texture;
    }
    if (isCustom() && quality.distortionMode !== "off" && !distortionAtlas) {
      distortionAtlas = bakeDistortionField(renderer, group, glassMeshes, glass.distortionScale, { layout: "atlas" });
      custom.setDistortionAtlas(distortionAtlas);
    }
  }
  const downloaded = Promise.all([modelLoaded, ...textureArrivals]).then(() => {});
  // a failed asset fails `ready` too, which the caller reports: this one is
  // not an unhandled rejection of its own when nothing waits on it
  downloaded.catch(() => {});
  // shaders compile while the textures are still downloading. A context lost
  // before the scene is ready fails it: the restored context has none of the
  // bakes the first frame needs (the environment's, the distortion's), and the
  // compiles and uploads under way were the lost context's (their waits
  // reject too: programsReady). (Lost after ready, three restores it and the
  // page draws on: landing.js's webglcontextrestored.)
  const loading = Promise.all([modelLoaded.then(compileShaders), peelFilled, ...textureUploads]).then(() => {});
  const ready = Promise.race([
    loading,
    contextLost.then(() => { throw new Error("the WebGL context was lost before the scene was ready"); }),
  ]);

  // Compiles every shader program render() uses, so no frame stalls on a
  // compile: the FBO passes (linear output, no tone mapping; the glass's back
  // faces) and the screen pass (tone mapped; its front faces), for every mesh
  // in the scene, including the hidden LED's square (it shares the drive's
  // programs, as the overlays do: makeUsbMaterials), and for the backdrop.
  // Where the browser supports KHR_parallel_shader_compile the compiles run
  // off the main thread; resolves once every program has linked.
  function compileShaders() {
    issueCompiles(scene);
    return programsReady();
  }

  // Resolves once every program made so far has compiled and linked, and
  // has had its first use (three's error check and uniform lookup), here
  // rather than in the first frame. That first use asks the GPU process and
  // waits for its answer, which comes only once the GPU process is done
  // with the work queued before it: made as soon as a program reported its
  // compile complete (KHR_parallel_shader_compile), it blocked the main
  // thread ~0.3-0.45 s while the GPU process went on with the others (and
  // with more of this one: all of them report complete well before it is
  // done). So it waits without blocking: for every program's completion
  // status, then for a fence (a sync object, polled) behind all of that
  // work; then the first uses take a few ms. (Without the extension,
  // Safari's and Firefox's case, isReady() is true at once; the fence still
  // keeps the wait off the main thread where the compile is queued.)
  // Rejects if the context is lost meanwhile: the programs and the fence
  // waited on are the lost context's, and would never report ready (a
  // context lost mid-load once left the page polling a dead fence forever).
  function programsReady() {
    const gl = renderer.getContext();
    const losses = contextLosses;
    return new Promise((resolve, reject) => {
      let fence = null;
      const poll = () => {
        if (contextLosses !== losses || gl.isContextLost()) {
          reject(new Error("the WebGL context was lost while the shaders compiled"));
          return;
        }
        const programs = renderer.info.programs;
        if (!programs.every((p) => p.isReady())) {
          setTimeout(poll, 10);
          return;
        }
        if (!fence) {
          fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
          gl.flush();
        }
        if (gl.getSyncParameter(fence, gl.SYNC_STATUS) !== gl.SIGNALED) {
          setTimeout(poll, 10);
          return;
        }
        gl.deleteSync(fence);
        for (const p of programs) p.getUniforms();
        resolve();
      };
      poll();
    });
  }

  // compiles the programs drawing `objects` (with the scene's lights and
  // environment) in each pass: into the FBOs (linear, the glass's back
  // faces) and to the screen (the glass's front faces)
  function issueCompiles(objects) {
    const material = glassMaterial();
    const side = material.side;
    renderer.setRenderTarget(fboBack);
    material.side = THREE.BackSide;
    renderer.compile(objects, camera, scene);
    renderer.setRenderTarget(null);
    material.side = THREE.FrontSide;
    renderer.compile(objects, camera, scene);
    material.side = side;
  }

  // Resizes the canvas AND the transmission FBOs. Sizes come from the
  // arguments, never the window: the studio renders at export sizes.
  //   transmissionPixelRatio — lets the FBOs keep their resolution when the
  //     canvas alone is downscaled (the landing page's adaptive resolution)
  //   updateStyle — false leaves the canvas's CSS size to the page (the
  //     studio shows export-sized buffers scaled into its preview)
  // The quality's renderScale and transmissionScale apply on top.
  let lastSize = null; // setSize's arguments, kept for quality changes
  function setSize(width, height, pixelRatio, { transmissionPixelRatio = pixelRatio, updateStyle = true } = {}) {
    lastSize = { width, height, pixelRatio, transmissionPixelRatio, updateStyle };
    applySize();
  }
  function applySize() {
    if (!lastSize) return;
    const { width, height, transmissionPixelRatio, updateStyle } = lastSize;
    const pixelRatio = lastSize.pixelRatio * quality.renderScale;
    const current = renderer.getSize(new THREE.Vector2());
    // each call reallocates the canvas buffer (a visible black flash), so skip no-ops
    if (renderer.getPixelRatio() !== pixelRatio) renderer.setPixelRatio(pixelRatio);
    if (current.x !== width || current.y !== height) renderer.setSize(width, height, updateStyle);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    const w = Math.floor(width * transmissionPixelRatio * quality.transmissionScale);
    const h = Math.floor(height * transmissionPixelRatio * quality.transmissionScale);
    fboMain.setSize(w, h);
    fboBack.setSize(w, h);
  }

  // Changes some of the cost knobs (js/quality.js; throws on an unknown knob
  // or a bad value, changing nothing) and returns the new quality. Takes
  // effect from the next render(); a knob that changes the program compiles
  // it then. transmissionInterval is only recorded: the page's render loop
  // decides which frames refresh the transmission (render's option).
  function setQuality(partial) {
    const prev = quality;
    quality = resolveQuality(quality, partial);
    const swapped = quality.samples !== prev.samples;
    if (swapped) {
      const next = glassWithSamples(quality.samples);
      next.side = glass.side;
      glass = next;
      if (!isCustom()) for (const m of glassMeshes) m.material = next;
    }
    if (quality.renderScale !== prev.renderScale || quality.transmissionScale !== prev.transmissionScale) applySize();
    if (quality.halfFloat !== prev.halfFloat) {
      // a target's texture type is fixed when it is allocated: free both, and
      // the next render allocates them with the new type
      for (const target of [fboMain, fboBack]) {
        target.texture.type = quality.halfFloat ? THREE.HalfFloatType : THREE.UnsignedByteType;
        target.dispose();
      }
    }
    cavityUniforms.cavityStrength.value = quality.cavityStrength;
    const baked = quality.distortionMode === "baked";
    if (needsField() && glassMeshes.length) bakeDistortion();
    // a glass swapped in may have last compiled under other settings
    applyMaterialQuality(glass, swapped || quality.dispersion !== prev.dispersion || baked !== (prev.distortionMode === "baked"));
    custom?.setQuality(quality);
    return quality;
  }

  // Switches the glass (js/glass/look.js resolveGlass; throws on a bad
  // setting, changing nothing) and returns the new setting. Takes effect
  // from the next render().
  function setGlass(setting) {
    glassSetting = resolveGlass(setting);
    if (isCustom()) customGlass().setLook(glassSetting.look);
    for (const m of glassMeshes) m.material = glassMaterial();
    if (needsField() && glassMeshes.length) bakeDistortion();
    configureBuffers();
    return glassSetting;
  }

  // Compiles the programs `setting`'s glass draws with (resolveGlass's; throws
  // on a bad one), without switching to it: resolves once they are ready
  // (see programsReady), after which setGlass(setting) compiles nothing.
  // Where the browser compiles in parallel (KHR_parallel_shader_compile) the
  // page keeps drawing in the current glass meanwhile. Needs the model in.
  function prepareGlass(setting) {
    if (!glassMeshes.length) throw new Error("prepareGlass: the model isn't in yet");
    const wanted = resolveGlass(setting);
    const current = glassSetting;
    glassSetting = wanted;
    try {
      if (isCustom()) customGlass().setLook(wanted.look);
      for (const m of glassMeshes) m.material = glassMaterial();
      issueCompiles(scene);
    } finally {
      glassSetting = current;
      if (isCustom()) customGlass().setLook(current.look);
      for (const m of glassMeshes) m.material = glassMaterial();
    }
    return programsReady();
  }

  // the material-level knobs, onto `material` (the current glass); a program
  // lookup only when something it compiles from may have changed (a program
  // it already has is reused from three's cache: no compile)
  function applyMaterialQuality(material, recompile) {
    const maps = {
      normalMap: quality.orangePeel ? peel : null,
      aoMap: quality.ao ? aoTex : null,
      map: quality.cavity ? cavityTex : null,
    };
    for (const [slot, tex] of Object.entries(maps)) {
      if (material[slot] !== tex) {
        material[slot] = tex;
        recompile = true;
      }
    }
    material.distortion = quality.distortionMode === "off" ? 0 : DISTORTION;
    material.chromaticAberration = quality.dispersionStrength;
    if (recompile) material.needsUpdate = true;
  }

  // null shows the site's gradient backdrop; otherwise a CSS color string
  // (e.g. "#17171a") as a flat background
  function setBackground(color) {
    scene.background = color === null ? null : new THREE.Color(color);
    backdrop.visible = color === null;
  }

  // MeshTransmissionMaterial two-pass protocol:
  // 1. scene without the glass -> fboMain (background, USB metal, etc.)
  // 2. glass back faces refracting fboMain -> fboBack (the interior, refractive)
  // 3. glass front faces refracting fboBack -> screen
  // refreshTransmission: false reuses the previous frame's passes 1+2 (the
  // caller decides when that's imperceptible).
  // Passes 1+2 need no tone mapping, and get none: three applies
  // renderer.toneMapping only when drawing to the screen (or an XR target).
  function render({ refreshTransmission = true } = {}) {
    if (!glassMeshes.length) {
      renderer.render(scene, camera);
      return;
    }
    // (both layouts span the same box: the glass's)
    const field = distortionField ?? distortionAtlas;
    if (field) {
      group.updateWorldMatrix(true, false);
      fieldMatrix(distortionUniforms.distortionToField.value, group, field.box);
    }
    if (refreshTransmission) {
      for (const m of glassMeshes) m.visible = false;
      for (const m of overlayMeshes) m.visible = false;
      renderer.setRenderTarget(fboMain);
      renderer.render(scene, camera);

      for (const m of glassMeshes) m.visible = true;
      glassPass("back", fboMain.texture);
      renderer.setRenderTarget(fboBack);
      renderer.render(scene, camera);
      for (const m of overlayMeshes) m.visible = true;
    }

    glassPass("front", fboBack.texture);
    renderer.setRenderTarget(null);
    renderer.render(scene, camera);
  }

  // readies the glass for a pass: its "back" faces into fboBack, reading the
  // scene; its "front" faces to the screen, reading the back faces' buffer
  function glassPass(side, buffer) {
    if (isCustom()) {
      custom.pass(side, buffer, renderer, scene.environment);
      return;
    }
    glass.buffer = buffer;
    glass.side = side === "back" ? THREE.BackSide : THREE.FrontSide;
    glass.thickness = side === "back" ? BACKSIDE_THICKNESS : FRONT_THICKNESS;
  }

  // amount 0..1: the drive's LED lighting the resin red (0 = unlit, the
  // default; 1 = fully lit). Uniform/color writes only — cheap per frame.
  function setGlow(amount) {
    if (!(amount >= 0 && amount <= 1)) throw new RangeError(`setGlow: amount must be in 0..1, got ${amount}`);
    glowUniforms.glowAmount.value = amount;
    if (amount > 0) loadGlow();
    // the die, lighting the resin from inside (the transmission passes), and
    // the soft point the resin shows of it (drawn over the glass; unlit, not
    // drawn at all)
    for (const m of [usbLook.metal, usbLook.led]) m.emissive.copy(GLOW_COLOR).multiplyScalar(LED_INTENSITY * amount);
    usbLook.led.visible = amount > 0;
  }

  return {
    renderer, scene, camera, group, setSize, setBackground, setGlow, loadGlow, render, setQuality, setGlass, prepareGlass, modelLoaded, downloaded, ready,
    get quality() { return quality; },
    get glass() { return glassSetting; },
    transmissionTargets: Object.freeze({ main: fboMain, back: fboBack }),
    // { target, box } once distortionMode "baked" has baked it, else null (for tests)
    get distortionField() { return distortionField; },
  };
}

// An image file's bytes, decoded for a texture as stored: no premultiplied
// alpha (usb-color's alpha is real), no color conversion, rows top-down; the
// same pixels three's upload of an <img> gives for these flipY-false
// textures (perf/pixels.mjs). createImageBitmap decodes off the main thread:
// an <img> is decoded in its upload, on the main thread (the 2048² cavity
// bake: ~90 ms at a phone's CPU speed, on the first frame's critical path).
// Where createImageBitmap ignores its options (Safari before 17, Firefox
// before 98: three's GLTFLoader's rule) or is missing, an <img> of the
// bytes, decoded at upload as before.
const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
const safariVersion = /^((?!chrome|android).)*safari/i.test(ua) ? Number(ua.match(/Version\/(\d+)/)?.[1] ?? -1) : null;
const firefoxVersion = /Firefox\/(\d+)\./.test(ua) ? Number(ua.match(/Firefox\/(\d+)\./)[1]) : null;
const IMAGE_BITMAPS = typeof createImageBitmap === "function" && !(safariVersion !== null && safariVersion < 17) && !(firefoxVersion !== null && firefoxVersion < 98);
function decodeImage(blob) {
  if (IMAGE_BITMAPS) return createImageBitmap(blob, { premultiplyAlpha: "none", colorSpaceConversion: "none" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.src = url;
  return img.decode().then(() => img).finally(() => URL.revokeObjectURL(url));
}

// Glow shading, spliced in after MeshTransmissionMaterial's own chunks. The
// bake's channels (blender/glow.py, photon-traced from the LED): r = light
// scattered in the resin just under the surface, seen through the depth of
// resin along the view ray (1 / n·v, capped); g = light escaping the surface
// steeply, seen from any side; b = light escaping at grazing angles, seen
// only with the face edge-on.
const GLOW_PARS = /* glsl */ `
uniform float glowAmount;
uniform vec3 glowColor;
uniform vec3 glowWeights;
const float GLOW_PEAK = ${GLOW_PEAK.toFixed(2)};
`;
const GLOW_FRAGMENT = /* glsl */ `
#ifdef USE_EMISSIVEMAP
  vec3 glowBake = texture2D( emissiveMap, vEmissiveMapUv ).rgb;
  float glowNdotV = abs( dot( normal, normalize( vViewPosition ) ) );
  float glowPath = 1.0 / max( glowNdotV, 0.25 );
  float glowEdgeOn = ( 1.0 - glowNdotV ) * ( 1.0 - glowNdotV );
  float glowLevel = glowWeights.x * glowBake.r * glowPath
    + glowWeights.y * glowBake.g + glowWeights.z * glowBake.b * glowEdgeOn;
  // roll the fully lit level off toward GLOW_PEAK (linear when dim, saturating
  // when hot) so hot spots stay red the way a camera records an LED instead of
  // ACES bleaching them pink-white; glowAmount then dims it all linearly, like
  // the LED's drive current
  totalEmissiveRadiance = glowColor * glowAmount * GLOW_PEAK * ( 1.0 - exp( - glowLevel / GLOW_PEAK ) );
#endif
`;

// three's map_fragment for the cavity map (the glass's only `map`), its
// darkening scaled: the bake is 1 on open surface and down to 0.82 in the
// grooves, and mix() moves each texel's distance from 1 by cavityStrength.
// At 1 it is three's chunk exactly: 1 + (s - 1) * 1 is s in floating point
// (s - 1 is exact for s in 0.5..2, and so is adding the 1 back).
const CAVITY_PARS = /* glsl */ `
uniform float cavityStrength;
`;
const CAVITY_FRAGMENT = /* glsl */ `
#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D( map, vMapUv );
  diffuseColor *= vec4( mix( vec3( 1.0 ), sampledDiffuseColor.rgb, cavityStrength ), sampledDiffuseColor.a );
#endif
`;
// The glass's one isinf(), in MeshTransmissionMaterial's Beer's-law
// attenuation ("+Infinity = no attenuation"). On Chrome's ANGLE→Metal
// backend any isnan/isinf compiles the WHOLE fragment shader with Metal's
// fast math off (precise sin/exp/pow…): measured on an M1 Max, the full
// frame at the opening close-up costs ~20 ms instead of ~4.5 ms. ANGLE drops
// that flag when it reloads a program from its program cache, so only a
// browser's first compile is slow — which is every visitor's first visit,
// for the life of the page (perf/first-visit.mjs). Our attenuationDistance
// is finite (never the +Infinity sentinel), so a plain comparison keeps the
// branch's meaning without the isinf.
const ISINF_ATTENUATION = "isinf( attenuationDistance )";
const FINITE_ATTENUATION = "( attenuationDistance > 3.0e38 )";

function replaceChunk(source, chunk, replacement) {
  if (!source.includes(chunk)) throw new Error(`shader chunk ${chunk} not found`);
  return source.replace(chunk, replacement);
}

// MeshTransmissionMaterial's refraction loop with one buffer lookup per sample
// instead of three. Its red lookup refracts with the base IOR; green and blue
// each repeat the whole lookup with the IOR raised by the chromatic
// aberration. Without dispersion all three channels come from the red
// lookup — what chromaticAberration = 0 renders, at a third of the lookups
// (the compiler can't drop them for a uniform that happens to be 0). Matched
// against @pmndrs/vanilla 1.25.0's source: throws if that ever changes.
function withoutDispersion(fragment) {
  const once = (source, pattern, replacement) => {
    const found = source.match(new RegExp(pattern.source, "g"))?.length ?? 0;
    if (found !== 1) throw new Error(`dispersion patch: expected one match of ${pattern}, found ${found}`);
    return source.replace(pattern, replacement);
  };
  fragment = once(fragment, /transmissionR = (getIBLVolumeRefraction\([\s\S]*?\))\.r;/,
    "vec3 transmissionRGB = $1.rgb;\n          transmissionR = transmissionRGB.r;");
  fragment = once(fragment, /transmissionG = getIBLVolumeRefraction\([\s\S]*?\)\.g;/, "transmissionG = transmissionRGB.g;");
  return once(fragment, /transmissionB = getIBLVolumeRefraction\([\s\S]*?\)\.b;/, "transmissionB = transmissionRGB.b;");
}

// The gradient backdrop, drawn as three draws a texture scene.background
// (WebGLBackground's full-screen plane: first, no depth test or write, no
// blending, not tone mapped as the texture is sRGB), but with ONE program
// for both render targets: three's BackgroundMaterial is a ShaderMaterial,
// whose program three keys on the output color space, so it compiled once
// into the linear buffers and once for the sRGB screen. Here the screen's
// sRGB encoding is applied in the shader when toScreen (as makeUsbMaterials
// does). With scene.background null three clears to its clear color, black,
// as it did with the texture; the backdrop then covers every pixel.
const BACKDROP_VERTEX = /* glsl */ `
precision highp float;
in vec3 position;
in vec2 uv;
out vec2 vUv;
void main() {
  // three's uv transform (identity: no texture offset/repeat) is exact
  vUv = uv;
  gl_Position = vec4( position.xy, 1.0, 1.0 );
}
`;
const BACKDROP_FRAGMENT = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D t2D;
uniform bool toScreen;
in vec2 vUv;
layout(location = 0) out highp vec4 pc_fragColor;
vec4 sRGBTransferOETF( in vec4 value ) {
  return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
}
void main() {
  pc_fragColor = texture( t2D, vUv );
  if ( toScreen ) pc_fragColor = sRGBTransferOETF( pc_fragColor );
}
`;
function makeBackdrop(texture) {
  const uniforms = { t2D: { value: texture }, toScreen: { value: false } };
  const material = new THREE.RawShaderMaterial({
    name: "Backdrop",
    glslVersion: THREE.GLSL3,
    vertexShader: BACKDROP_VERTEX,
    fragmentShader: BACKDROP_FRAGMENT,
    uniforms,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  material.onBeforeRender = (renderer) => {
    const toScreen = renderer.getRenderTarget() === null;
    if (uniforms.toScreen.value === toScreen) return;
    uniforms.toScreen.value = toScreen;
    material.uniformsNeedUpdate = true;
  };
  const geometry = new THREE.PlaneGeometry(2, 2);
  geometry.deleteAttribute("normal");
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "backdrop";
  mesh.frustumCulled = false;
  // first, before anything else in the scene (three put its own first)
  mesh.renderOrder = -Number.MAX_SAFE_INTEGER;
  return mesh;
}

// A mesh's geometry as three keys programs on it: the vertex attributes the
// GLBs have (position, normal, uv: float32; uint16 indices); one degenerate
// triangle
function standInGeometry() {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(9), 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(9), 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(6), 2));
  geometry.setIndex(new THREE.BufferAttribute(new Uint16Array([0, 1, 2]), 1));
  return geometry;
}

// orange-peel micro-texture (js/orange-peel.js): computed in JS, uploaded as
// data. (It was drawn through 2D canvases, blurred by a canvas filter: reading
// that back cost a cold first visit ~175 ms, the main thread waiting on the
// GPU.) Sampled as the canvas texture was: mipmapped, rows bottom-up (flipY).
// Made empty: fillOrangePeel computes its texels, before the first frame.
function makeOrangePeel() {
  const tex = new THREE.DataTexture(null, ORANGE_PEEL_SIZE, ORANGE_PEEL_SIZE);
  tex.flipY = true;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(13, 13);
  return tex;
}
function fillOrangePeel(tex) {
  tex.image.data = orangePeelNormals();
  tex.needsUpdate = true;
}

// --- the USB drive -----------------------------------------------------
// The plug and the drive's body inside the stem, modelled at the real part's
// dimensions and baked into one texture set by blender/usb.py (assets/usb.glb,
// usb-color, usb-orm, usb-led), so one material draws all of it: nickel,
// the off-white tongue and its gold contacts, the dark inside, the LED. Its
// copies below differ only in uniforms and render state: all of the drive is
// one shader program per render target.
// The textures are on the GLB's UVs, unflipped (glTF convention).
// The metal's environment reflection is three's scene.environmentIntensity,
// 1: blender/usb.py bakes with ENV 1.6, and the page's MeshStandardMaterial
// asked for 1.6 too, but three r169 overrides a material's envMapIntensity
// with scene.environmentIntensity whenever the map is scene.environment,
// so 1 is what the page has always drawn.
// The body is drawn again over the glass (see placeUSB), lighter and at
// partial opacity: IRL it sits ~2 mm deep, so it reads hazed, not warped.
const USB_SEEN_OPACITY = 0.38;
const USB_SEEN_GAIN = 1.55; // its old #b2b2ba over the body's #92929a, linear

// The drive's shader: three r169's MeshStandardMaterial as the drive used
// it, written out with only what it used (the color, ORM and LED maps, the
// PMREM environment, the two directional lights, no ambient light), in
// three's operation order so it draws the very same pixels (perf/pixels.mjs).
// One program for both render targets: a RawShaderMaterial's program key is
// its defines alone, and the screen's ACES tone mapping and sRGB encoding
// are applied here when toScreen (three applies neither into the linear
// half-float buffers). MeshStandardMaterial compiled once per target, each
// ~72-75k characters: ~150 ms each of a cold first visit.
const USB_VERTEX = /* glsl */ `
precision highp float;
precision highp int;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;
in vec3 position;
in vec3 normal;
in vec2 uv;
out vec3 vViewPosition;
out vec3 vNormal;
out vec2 vUv;
void main() {
  // three's uv transform (identity: no texture offset/repeat) is exact
  vUv = uv;
  vNormal = normalize( normalMatrix * vec3( normal ) );
  vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
  gl_Position = projectionMatrix * mvPosition;
  vViewPosition = - mvPosition.xyz;
}
`;
const USB_FRAGMENT = /* glsl */ `
precision highp float;
precision highp int;
precision highp sampler2D;
#define texture2D texture
#define texture2DGradEXT textureGrad
layout(location = 0) out highp vec4 pc_fragColor;

uniform mat4 viewMatrix;
uniform bool toScreen;
uniform float toneMappingExposure;
uniform vec3 diffuse;
uniform vec3 emissive;
uniform float opacity;
uniform sampler2D map;
uniform sampler2D ormMap; // r ambient occlusion, g roughness, b metalness
uniform sampler2D emissiveMap;
uniform sampler2D envMap;
uniform float envMapIntensity;
struct DirectionalLight {
  vec3 direction;
  vec3 color;
};
uniform DirectionalLight directionalLights[ 2 ];

in vec3 vViewPosition;
in vec3 vNormal;
in vec2 vUv;

float pow2( const in float x ) { return x*x; }
vec3 inverseTransformDirection( in vec3 dir, in mat4 matrix ) {
  return normalize( ( vec4( dir, 0.0 ) * matrix ).xyz );
}
vec3 BRDF_Lambert( const in vec3 diffuseColor ) {
  return 0.3183098861837907 * diffuseColor;
}
vec3 F_Schlick( const in vec3 f0, const in float f90, const in float dotVH ) {
  float fresnel = exp2( ( - 5.55473 * dotVH - 6.98316 ) * dotVH );
  return f0 * ( 1.0 - fresnel ) + ( f90 * fresnel );
}
float V_GGX_SmithCorrelated( const in float alpha, const in float dotNL, const in float dotNV ) {
  float a2 = pow2( alpha );
  float gv = dotNL * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNV ) );
  float gl = dotNV * sqrt( a2 + ( 1.0 - a2 ) * pow2( dotNL ) );
  return 0.5 / max( gv + gl, 1e-6 );
}
float D_GGX( const in float alpha, const in float dotNH ) {
  float a2 = pow2( alpha );
  float denom = pow2( dotNH ) * ( a2 - 1.0 ) + 1.0;
  return 0.3183098861837907 * a2 / pow2( denom );
}
vec3 BRDF_GGX( const in vec3 lightDir, const in vec3 viewDir, const in vec3 normal, const in vec3 f0, const in float f90, const in float roughness ) {
  float alpha = pow2( roughness );
  vec3 halfDir = normalize( lightDir + viewDir );
  float dotNL = clamp( dot( normal, lightDir ), 0.0, 1.0 );
  float dotNV = clamp( dot( normal, viewDir ), 0.0, 1.0 );
  float dotNH = clamp( dot( normal, halfDir ), 0.0, 1.0 );
  float dotVH = clamp( dot( viewDir, halfDir ), 0.0, 1.0 );
  vec3 F = F_Schlick( f0, f90, dotVH );
  float V = V_GGX_SmithCorrelated( alpha, dotNL, dotNV );
  float D = D_GGX( alpha, dotNH );
  return F * ( V * D );
}
vec2 DFGApprox( const in vec3 normal, const in vec3 viewDir, const in float roughness ) {
  float dotNV = clamp( dot( normal, viewDir ), 0.0, 1.0 );
  const vec4 c0 = vec4( - 1, - 0.0275, - 0.572, 0.022 );
  const vec4 c1 = vec4( 1, 0.0425, 1.04, - 0.04 );
  vec4 r = roughness * c0 + c1;
  float a004 = min( r.x * r.x, exp2( - 9.28 * dotNV ) ) * r.x + r.y;
  vec2 fab = vec2( - 1.04, 1.04 ) * a004 + r.zw;
  return fab;
}
float computeSpecularOcclusion( const in float dotNV, const in float ambientOcclusion, const in float roughness ) {
  return clamp( pow( dotNV + ambientOcclusion, exp2( - 16.0 * roughness - 1.0 ) ) - 1.0 + ambientOcclusion, 0.0, 1.0 );
}

#include <cube_uv_reflection_fragment>

vec3 RRTAndODTFit( vec3 v ) {
  vec3 a = v * ( v + 0.0245786 ) - 0.000090537;
  vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;
  return a / b;
}
vec3 ACESFilmicToneMapping( vec3 color ) {
  const mat3 ACESInputMat = mat3(
    vec3( 0.59719, 0.07600, 0.02840 ), vec3( 0.35458, 0.90834, 0.13383 ),
    vec3( 0.04823, 0.01566, 0.83777 )
  );
  const mat3 ACESOutputMat = mat3(
    vec3(  1.60475, -0.10208, -0.00327 ), vec3( -0.53108,  1.10813, -0.07276 ),
    vec3( -0.07367, -0.00605,  1.07602 )
  );
  color *= toneMappingExposure / 0.6;
  color = ACESInputMat * color;
  color = RRTAndODTFit( color );
  color = ACESOutputMat * color;
  return clamp( color, 0.0, 1.0 );
}
vec4 sRGBTransferOETF( in vec4 value ) {
  return vec4( mix( pow( value.rgb, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), value.rgb * 12.92, vec3( lessThanEqual( value.rgb, vec3( 0.0031308 ) ) ) ), value.a );
}

void main() {
  vec4 diffuseColor = vec4( diffuse, opacity );
  vec3 totalEmissiveRadiance = emissive;
  diffuseColor *= texture( map, vUv );
  vec4 orm = texture( ormMap, vUv );
  float roughnessFactor = orm.g; // roughness 1 × the map
  float metalnessFactor = orm.b; // metalness 1 × the map
  vec3 normal = normalize( vNormal );
  totalEmissiveRadiance *= texture( emissiveMap, vUv ).rgb;

  vec3 materialDiffuse = diffuseColor.rgb * ( 1.0 - metalnessFactor );
  vec3 dxy = max( abs( dFdx( normal ) ), abs( dFdy( normal ) ) );
  float geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );
  float roughness = max( roughnessFactor, 0.0525 );
  roughness += geometryRoughness;
  roughness = min( roughness, 1.0 );
  vec3 specularColor = mix( vec3( 0.04 ), diffuseColor.rgb, metalnessFactor );
  float specularF90 = 1.0;
  vec3 viewDir = normalize( vViewPosition );

  vec3 directDiffuse = vec3( 0.0 );
  vec3 directSpecular = vec3( 0.0 );
  for ( int i = 0; i < 2; i ++ ) {
    vec3 lightDir = directionalLights[ i ].direction;
    float dotNL = clamp( dot( normal, lightDir ), 0.0, 1.0 );
    vec3 irradiance = dotNL * directionalLights[ i ].color;
    directSpecular += irradiance * BRDF_GGX( lightDir, viewDir, normal, specularColor, specularF90, roughness );
    directDiffuse += irradiance * BRDF_Lambert( materialDiffuse );
  }

  vec3 iblIrradiance = 3.141592653589793 * textureCubeUV( envMap, inverseTransformDirection( normal, viewMatrix ), 1.0 ).rgb * envMapIntensity;
  vec3 reflectVec = reflect( - viewDir, normal );
  reflectVec = normalize( mix( reflectVec, normal, roughness * roughness ) );
  reflectVec = inverseTransformDirection( reflectVec, viewMatrix );
  vec3 radiance = textureCubeUV( envMap, reflectVec, roughness ).rgb * envMapIntensity;

  // RE_IndirectSpecular_Physical with three's multiple scattering
  vec2 fab = DFGApprox( normal, viewDir, roughness );
  vec3 FssEss = specularColor * fab.x + specularF90 * fab.y;
  float Ess = fab.x + fab.y;
  float Ems = 1.0 - Ess;
  vec3 Favg = specularColor + ( 1.0 - specularColor ) * 0.047619;
  vec3 Fms = FssEss * Favg / ( 1.0 - Ems * Favg );
  vec3 singleScattering = vec3( 0.0 ) + FssEss;
  vec3 multiScattering = vec3( 0.0 ) + Fms * Ems;
  vec3 cosineWeightedIrradiance = iblIrradiance * 0.3183098861837907;
  vec3 totalScattering = singleScattering + multiScattering;
  vec3 indirectDiffuse = materialDiffuse * ( 1.0 - max( max( totalScattering.r, totalScattering.g ), totalScattering.b ) ) * cosineWeightedIrradiance;
  vec3 indirectSpecular = radiance * singleScattering;
  indirectSpecular += multiScattering * cosineWeightedIrradiance;

  float ambientOcclusion = ( orm.r - 1.0 ) * 1.0 + 1.0;
  indirectDiffuse *= ambientOcclusion;
  float dotNV = clamp( dot( normal, viewDir ), 0.0, 1.0 );
  indirectSpecular *= computeSpecularOcclusion( dotNV, ambientOcclusion, roughness );

  vec3 outgoingLight = ( directDiffuse + indirectDiffuse ) + ( directSpecular + indirectSpecular ) + totalEmissiveRadiance;
  pc_fragColor = vec4( outgoingLight, diffuseColor.a );
  if ( toScreen ) {
    pc_fragColor.rgb = ACESFilmicToneMapping( pc_fragColor.rgb );
    pc_fragColor = sRGBTransferOETF( pc_fragColor );
  }
}
`;

function makeUsbMaterials(loadTexture) {
  const map = loadTexture("usb-color.webp");
  map.colorSpace = THREE.SRGBColorSpace;
  // R ambient occlusion, G roughness, B metalness
  const orm = loadTexture("usb-orm.webp");
  // where the LED is: setGlow scales its emission (0 = unlit: none at all)
  const ledMask = loadTexture("usb-led.webp");
  // its look: `color` and `emissive` (Colors, linear) as a three material
  // has them, opacity and the depth flags as usual
  const usbMaterial = ({ color = 1, opacity = 1, depth = true }) => {
    const uniforms = {
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.lights),
      toScreen: { value: false },
      toneMappingExposure: { value: 1 },
      diffuse: { value: new THREE.Color().setScalar(color) },
      emissive: { value: new THREE.Color(0x000000) },
      opacity: { value: opacity },
      map: { value: map },
      ormMap: { value: orm },
      emissiveMap: { value: ledMask },
      envMap: { value: null },
      envMapIntensity: { value: 1 },
    };
    const material = new THREE.RawShaderMaterial({
      name: "UsbMaterial",
      glslVersion: THREE.GLSL3,
      vertexShader: USB_VERTEX,
      fragmentShader: USB_FRAGMENT,
      uniforms,
      lights: true,
      toneMapped: false,
      // as the MeshStandardMaterial was: transparent, so the drive draws in
      // the transparent list (after the glass), depth-tested and written
      // like any opaque mesh (opacity 1: the blend keeps only the drive)
      transparent: true,
      depthTest: depth,
      depthWrite: depth,
    });
    // three's CUBEUV_* defines for the environment (see setEnvironment)
    material.defines = {};
    material.emissive = uniforms.emissive.value;
    // the pass: the screen (tone mapped, sRGB) or a linear buffer
    material.onBeforeRender = (renderer, scene) => {
      const toScreen = renderer.getRenderTarget() === null;
      const environment = scene.environment;
      if (uniforms.toScreen.value !== toScreen || uniforms.envMap.value !== environment) {
        uniforms.toScreen.value = toScreen;
        uniforms.envMap.value = environment;
        material.uniformsNeedUpdate = true;
      }
      uniforms.envMapIntensity.value = scene.environmentIntensity;
      uniforms.toneMappingExposure.value = renderer.toneMappingExposure;
    };
    return material;
  };
  const metal = usbMaterial({});
  const seen = usbMaterial({ color: USB_SEEN_GAIN, opacity: USB_SEEN_OPACITY, depth: false });
  // the LED's square (usb.glb's "led": black, its alpha a soft dot) over it
  const led = usbMaterial({ depth: false });
  led.visible = false;
  // the environment's size, as three's CUBEUV_* defines (WebGLProgram
  // generateCubeUVSize): before the first compile; the same for the life of
  // the page (PMREM fromScene's atlas)
  function setEnvironment(texture) {
    const maxMip = Math.log2(texture.image.height) - 2;
    const defines = {
      ENVMAP_TYPE_CUBE_UV: "",
      CUBEUV_TEXEL_WIDTH: 1 / (3 * Math.max(2 ** maxMip, 7 * 16)),
      CUBEUV_TEXEL_HEIGHT: 1 / texture.image.height,
      CUBEUV_MAX_MIP: `${maxMip}.0`,
    };
    for (const material of [metal, seen, led]) {
      if (JSON.stringify(material.defines) === JSON.stringify(defines)) continue;
      material.defines = defines;
      material.needsUpdate = true;
    }
  }
  return { metal, seen, led, orm, setEnvironment };
}

// Places the drive (usb.glb's scene: meshes "plug", "body" and "led", in mm,
// its origin at the stem's bottom face) under the stem, whose bottom is at
// bottomY; mm2u is world units per millimetre. What is drawn over the glass
// (the body's see-through copy, the LED's point) is appended to
// overlayMeshes: the transmission passes skip it (see render()).
function placeUSB(usb, mm2u, bottomY, { metal, seen, led }, overlayMeshes) {
  const part = (name, material) => {
    const mesh = usb.getObjectByName(name);
    if (!mesh?.isMesh) throw new Error(`usb.glb: no mesh "${name}"`);
    mesh.material = material;
    return mesh;
  };
  part("plug", metal);
  const body = part("body", metal);
  const bodySeen = new THREE.Mesh(body.geometry, seen);
  bodySeen.renderOrder = 5;
  body.add(bodySeen);
  // front-facing only: from behind, the square is culled
  const ledSeen = part("led", led);
  ledSeen.renderOrder = 6;
  overlayMeshes.push(bodySeen, ledSeen);
  usb.scale.setScalar(mm2u);
  usb.position.y = bottomY;
  return usb;
}
